import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  HelpCircle,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { contactApi, publicApi } from '../api/client';
import assetUrl from '../assetUrl';
import './ContactPage.css';

const BANNER_IMAGE = '/images/home-zigzag-1.jpg';
const WHATSAPP_URL = 'https://wa.me/21671234567';

const SUBJECTS = [
  { value: 'general', label: 'Question générale' },
  { value: 'quote', label: 'Demande de devis' },
  { value: 'order', label: 'Suivi de commande' },
  { value: 'sworn', label: 'Traduction assermentée' },
  { value: 'partnership', label: 'Partenariat' },
  { value: 'other', label: 'Autre' },
];

export default function ContactPage() {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const c = t.contact;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: '', email: '', subject: 'general', subjectOther: '', orderRef: '', message: '' });

  useEffect(() => {
    let mounted = true;
    publicApi.settings()
      .then((res) => { if (mounted && res?.settings) setSettings(res.settings); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const phone = settings.contactPhone || '+216 71 234 567';
  const email = settings.contactEmail || 'contact@tarjemlek.tn';
  const whatsappUrl = settings.socialWhatsApp || WHATSAPP_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()
      || (form.subject === 'other' && !form.subjectOther.trim())) {
      setError(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await contactApi.send({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject === 'other' ? form.subjectOther.trim().slice(0, 80) : form.subject,
        orderRef: form.orderRef.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      setError(err.data?.error === 'invalidEmail'
        ? 'Adresse e-mail invalide.'
        : "Erreur lors de l'envoi. Réessayez dans un instant.");
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', email: '', subject: 'general', subjectOther: '', orderRef: '', message: '' });
    setError('');
    setSent(false);
  };

  return (
    <div className={`contact-page${isRTL ? ' rtl' : ''}`}>
      <header className="contact-banner">
        <div
          className="contact-banner-bg"
          style={{ backgroundImage: `url(${assetUrl(BANNER_IMAGE)})` }}
          aria-hidden="true"
        />
        <div className="contact-banner-veil" aria-hidden="true" />

        <div className="container contact-banner-inner">
          <span className="contact-banner-badge">{c.badge}</span>
          <h1>{c.title}</h1>
          <p>{c.subtitle}</p>
          <nav className="contact-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">{c.breadcrumbHome}</Link>
            <ChevronRight size={12} aria-hidden="true" />
            <span>{c.title}</span>
          </nav>
        </div>
      </header>

      <section className="contact-body">
        <div className="container contact-layout">
          <aside className="contact-info">
            <h2 className="contact-info-title">{c.infoTitle}</h2>

            <a className="contact-card" href={`tel:${phone.replace(/\s/g, '')}`}>
              <span className="contact-card-icon">
                <Phone size={18} />
              </span>
              <span className="contact-card-text">
                <small>{c.phoneLabel}</small>
                <strong>{phone}</strong>
              </span>
            </a>

            <a className="contact-card" href={`mailto:${email}`}>
              <span className="contact-card-icon">
                <Mail size={18} />
              </span>
              <span className="contact-card-text">
                <small>{c.emailLabel}</small>
                <strong>{email}</strong>
              </span>
            </a>

            <a
              className="contact-whatsapp"
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle size={18} />
              {c.whatsapp}
            </a>
          </aside>

          <div className="contact-form-card">
            {sent ? (
              <div className="contact-success">
                <span className="contact-success-icon">
                  <CheckCircle size={38} />
                </span>
                <p>{c.success}</p>
                <p className="contact-success-mail">
                  <Mail size={15} />
                  Votre demande a été transmise par e-mail à notre équipe.
                </p>
                <button type="button" className="btn btn-outline" onClick={resetForm}>
                  {c.successAgain}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <header className="contact-form-head">
                  <h2>{c.formTitle || 'Envoyez-nous un message'}</h2>
                  <p>{c.formHint}</p>
                </header>

                <div className="contact-form-grid">
                  <label className="contact-field">
                    <span>{c.name || 'Nom complet'} *</span>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                  </label>
                  <label className="contact-field">
                    <span>{c.email || 'Adresse e-mail'} *</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </label>
                  <label className="contact-field contact-field-full">
                    <span>Objet de votre demande *</span>
                    <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
                      {SUBJECTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </label>
                  {form.subject === 'other' && (
                    <label className="contact-field contact-field-full">
                      <span>Précisez l'objet *</span>
                      <input
                        value={form.subjectOther}
                        placeholder="Ex. Attestation, tarif spécial…"
                        onChange={(e) => setForm({ ...form, subjectOther: e.target.value })}
                      />
                    </label>
                  )}
                  {form.subject === 'order' && (
                    <label className="contact-field contact-field-full">
                      <span>Référence de commande <small>({c.optional})</small></span>
                      <input
                        value={form.orderRef}
                        placeholder="ORD-…"
                        onChange={(e) => setForm({ ...form, orderRef: e.target.value })}
                      />
                    </label>
                  )}
                  <label className="contact-field contact-field-full contact-field-message">
                    <span>{c.message || 'Votre message'} *</span>
                    <textarea
                      rows={7}
                      placeholder={c.messagePlaceholder}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                    />
                  </label>
                </div>

                {error && (
                  <p className="contact-error" role="alert">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                  </p>
                )}
                <button type="submit" className="btn btn-primary contact-submit" disabled={busy}>
                  <Send size={17} />
                  {busy ? '…' : c.send}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="contact-extra">
        <div className="container contact-extra-grid">
          <div className="contact-extra-card">
            <HelpCircle size={18} />
            <h3>Questions fréquentes</h3>
            <details>
              <summary>Quels sont les délais de traduction ?</summary>
              <p>Ils dépendent du volume et du service choisi (standard, rapide ou urgent). Le délai estimé apparaît lors de votre commande ou dans votre devis.</p>
            </details>
            <details>
              <summary>Quels sont les tarifs ?</summary>
              <p>Les tarifs varient selon la langue, le type de document et le délai. Demandez un devis gratuit depuis la page Devis.</p>
            </details>
            <details>
              <summary>Comment fonctionne le paiement ?</summary>
              <p>La prestation démarre après confirmation du paiement (carte bancaire en ligne ou virement avec référence de suivi).</p>
            </details>
            <details>
              <summary>Proposez-vous la traduction assermentée ?</summary>
              <p>Oui, avec les formalités de certification selon le document et le pays de destination.</p>
            </details>
          </div>

          <div className="contact-extra-card">
            <MessageCircle size={18} />
            <h3>Suivi d'une commande</h3>
            <p>Clients : suivez vos commandes en cours depuis votre espace.</p>
            {user ? (
              <Link className="btn btn-outline" to="/app/orders">Mes commandes</Link>
            ) : (
              <Link className="btn btn-outline" to="/suivi">Suivre ma commande</Link>
            )}
          </div>

          <div className="contact-extra-card">
            <MapPin size={18} />
            <h3>Nos coordonnées</h3>
            <p>{settings.contactAddress || 'Tunis, Tunisie'}</p>
          </div>

          <div className="contact-extra-card">
            <ShieldCheck size={18} />
            <h3>Protection des données</h3>
            <p>
              Les informations transmises servent uniquement à traiter votre demande.
              Consultez notre <Link to="/privacy">politique de confidentialité</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
